import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { brandColor } from "@/lib/utils-msip";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Dealers, Users } from "@/pages/AdminOrg";
import { ManpowerAdmin } from "@/pages/AdminManpower";
import DpcAdmin from "@/pages/DpcAdmin";
import { DataAdmin } from "@/pages/AdminData";
import { UsageAdmin } from "@/pages/AdminUsage";
import { ServiceAdmin } from "@/pages/AdminService";
import { StockAdmin } from "@/pages/AdminStock";

const DISTRICTS = ["ALLEPPEY", "ERNAKULAM", "IDUKKI", "KANNUR", "KASARAGOD", "KOLLAM", "KOTTAYAM", "KOZHIKODE", "MALAPPURAM", "PALAKKAD", "PATHANAMTHITTA", "TRICHUR", "TRIVANDRUM", "WAYANAD"];

function Targets() {
  const { meta } = useApp();
  const [targets, setTargets] = useState([]);
  const [form, setForm] = useState({ brand: "KIA", market: "KERALA", share_pct_total: "" });
  const load = useCallback(() => api.get("/admin/targets").then(({ data }) => setTargets(data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put("/admin/targets", {
        brand: form.brand, market: form.market || "KERALA",
        share_pct_total: parseFloat(form.share_pct_total),
        share_pct_exm: null,
      });
      toast.success("Target saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="border bg-card rounded-md p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Brand</label>
          <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
            <SelectTrigger className="h-9 w-32 text-sm" data-testid="target-brand-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(meta?.brands || []).map((b) => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Market (KERALA / RTO / district)</label>
          <Input value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value.toUpperCase() })} className="h-9 w-44 text-sm" data-testid="target-market-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Target % (Total TIV)</label>
          <Input type="number" step="0.1" value={form.share_pct_total} onChange={(e) => setForm({ ...form, share_pct_total: e.target.value })} className="h-9 w-28 text-sm" data-testid="target-total-input" />
        </div>
        <Button onClick={save} disabled={!form.share_pct_total} data-testid="target-save-btn">Save target</Button>
      </div>
      <div className="border bg-card rounded-md overflow-auto">
        <table className="w-full text-sm">
          <thead className="border-b"><tr>{["Brand", "Market", "Target %", "Note"].map((h) => <th key={h} className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{h}</th>)}</tr></thead>
          <tbody>
            {targets.map((t) => (
              <tr key={t.id} className="border-b last:border-0 hover:bg-muted/50">
                <td className="px-3 py-1.5 font-semibold" style={{ color: brandColor(t.brand) }}>{t.brand}</td>
                <td className="px-3 py-1.5">{t.market}</td>
                <td className="px-3 py-1.5 num">{t.share_pct_total}%</td>
                <td className="px-3 py-1.5 text-muted-foreground text-xs">{t.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Brands() {
  const { meta, refreshMeta } = useApp();
  const [form, setForm] = useState({ name: "", color: "#808285", rival: "" });
  const add = async () => {
    try {
      await api.post("/admin/brands", { name: form.name, color: form.color, rival: form.rival || null });
      toast.success("Brand saved");
      setForm({ name: "", color: "#808285", rival: "" });
      refreshMeta();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="border bg-card rounded-md p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Brand name</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })} className="h-9 w-36 text-sm" data-testid="brand-name-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Color</label>
          <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-14 border rounded cursor-pointer" data-testid="brand-color-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Default rival</label>
          <Select value={form.rival || "none"} onValueChange={(v) => setForm({ ...form, rival: v === "none" ? "" : v })}>
            <SelectTrigger className="h-9 w-32 text-sm" data-testid="brand-rival-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {(meta?.brands || []).map((b) => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={add} disabled={!form.name} data-testid="brand-add-btn">Save brand</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {(meta?.brands || []).map((b) => (
          <div key={b.name} className="border bg-card rounded-md px-3 py-2 flex items-center gap-2 text-sm">
            <span className="h-3 w-3 rounded-full" style={{ background: b.color }} />
            <b>{b.name}</b>
            {b.rival && <span className="text-xs text-muted-foreground">rival: {b.rival}</span>}
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">OTHERS is computed automatically (TOTAL − tracked brands) and never stored.</p>
    </div>
  );
}

function Geography() {
  const { meta, refreshMeta } = useApp();
  const [search, setSearch] = useState("");
  const [merge, setMerge] = useState({ source: "", target: "" });
  const geos = useMemo(() => (meta?.geographies || []).filter((g) => !search || g.rto_code.toLowerCase().includes(search.toLowerCase())), [meta, search]);

  const update = async (code, patch) => {
    try {
      await api.put(`/admin/geographies/${encodeURIComponent(code)}`, patch);
      toast.success(`${code} updated`);
      refreshMeta();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doMerge = async () => {
    try {
      await api.post("/admin/geographies/merge", { source_code: merge.source, target_code: merge.target });
      toast.success(`Merged ${merge.source} → ${merge.target}`);
      setMerge({ source: "", target: "" });
      refreshMeta();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="border bg-card rounded-md p-4 flex flex-wrap items-end gap-3">
        <div className="text-sm font-semibold">Merge alias:</div>
        <Select value={merge.source || undefined} onValueChange={(v) => setMerge({ ...merge, source: v })}>
          <SelectTrigger className="h-9 w-52 text-sm" data-testid="merge-source-select"><SelectValue placeholder="Source (duplicate)…" /></SelectTrigger>
          <SelectContent>{(meta?.geographies || []).map((g) => <SelectItem key={g.rto_code} value={g.rto_code} disabled={g.rto_code === merge.target}>{g.rto_code}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">→</span>
        <Select value={merge.target || undefined} onValueChange={(v) => setMerge({ ...merge, target: v })}>
          <SelectTrigger className="h-9 w-52 text-sm" data-testid="merge-target-select"><SelectValue placeholder="Target (keep)…" /></SelectTrigger>
          <SelectContent>{(meta?.geographies || []).map((g) => <SelectItem key={g.rto_code} value={g.rto_code} disabled={g.rto_code === merge.source}>{g.rto_code}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={doMerge} disabled={!merge.source || !merge.target} variant="outline" data-testid="merge-btn">Merge</Button>
        <Input placeholder="Search RTO…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-48 text-sm ml-auto" data-testid="geo-search-input" />
      </div>
      <div className="border bg-card rounded-md overflow-auto max-h-[60vh]">
        <table className="w-full text-sm">
          <thead className="border-b sticky top-0 bg-card"><tr>{["RTO", "District", "State", "Incheon"].map((h) => <th key={h} className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{h}</th>)}</tr></thead>
          <tbody>
            {geos.map((g) => (
              <tr key={g.rto_code} className="border-b last:border-0 hover:bg-muted/50">
                <td className="px-3 py-1.5 font-medium">{g.rto_code}</td>
                <td className="px-3 py-1.5">
                  {g.is_state_row ? (
                    <span className="text-muted-foreground text-xs">{g.district}</span>
                  ) : (
                    <Select value={DISTRICTS.includes(g.district) ? g.district : undefined} onValueChange={(v) => update(g.rto_code, { district: v })}>
                      <SelectTrigger className="h-7 w-44 text-xs" data-testid={`geo-district-${g.rto_code}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{DISTRICTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground text-xs">{g.state}</td>
                <td className="px-3 py-1.5 text-xs">
                  <button
                    onClick={() => update(g.rto_code, { in_incheon_territory: !g.in_incheon_territory })}
                    className={`rounded border px-2 py-0.5 transition-colors duration-200 ${g.in_incheon_territory ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:bg-secondary"}`}
                    data-testid={`geo-territory-${g.rto_code}`}
                  >
                    {g.in_incheon_territory ? "✓" : "—"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Scoring() {
  const [w, setW] = useState(null);
  useEffect(() => { api.get("/admin/weights").then(({ data }) => setW(data)); }, []);
  if (!w) return null;
  const total = ["achievement", "delta", "rival_gap", "consistency"].reduce((s, k) => s + (parseFloat(w[k]) || 0), 0);
  const save = async () => {
    try {
      await api.put("/admin/weights", { achievement: +w.achievement, delta: +w.delta, rival_gap: +w.rival_gap, consistency: +w.consistency });
      toast.success("League weights saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const FIELDS = [
    ["achievement", "Target achievement (capped 125%)"],
    ["delta", "Δ share vs prior month"],
    ["rival_gap", "Rival-gap improvement"],
    ["consistency", "Consistency (6 mo ≥ target)"],
  ];
  return (
    <div className="border bg-card rounded-md p-5 max-w-lg space-y-4">
      <h3 className="font-heading text-sm font-semibold">RTO League scoring weights</h3>
      {FIELDS.map(([k, label]) => (
        <div key={k} className="flex items-center gap-3">
          <label className="text-sm w-64">{label}</label>
          <Input type="number" value={w[k]} onChange={(e) => setW({ ...w, [k]: e.target.value })} className="h-9 w-24 text-sm" data-testid={`weight-${k}-input`} />
          <span className="text-xs text-muted-foreground">pts</span>
        </div>
      ))}
      <div className="flex items-center gap-3">
        <span className={`text-xs ${total === 100 ? "text-emerald-700" : "text-amber-700"}`} data-testid="weights-total">Total: {total} pts {total !== 100 ? "(recommended: 100)" : ""}</span>
        <Button onClick={save} data-testid="weights-save-btn">Save weights</Button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [params] = useSearchParams();
  return (
    <div className="space-y-5" data-testid="admin-page">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">Admin</h1>
      <Tabs defaultValue={params.get("tab") || "targets"}>
        <div className="overflow-x-auto max-w-full"><TabsList className="w-max">
          <TabsTrigger value="targets" data-testid="tab-targets">Targets</TabsTrigger>
          <TabsTrigger value="brands" data-testid="tab-brands">Brands</TabsTrigger>
          <TabsTrigger value="geography" data-testid="tab-geography">Geography</TabsTrigger>
          <TabsTrigger value="dealers" data-testid="tab-dealers">Dealers</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="manpower" data-testid="tab-manpower">Manpower</TabsTrigger>
          <TabsTrigger value="dpc" data-testid="tab-dpc">DPC</TabsTrigger>
          <TabsTrigger value="service" data-testid="tab-service">Service</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
          <TabsTrigger value="scoring" data-testid="tab-scoring">Scoring</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data">Data</TabsTrigger>
          <TabsTrigger value="usage" data-testid="tab-usage">Usage</TabsTrigger>
        </TabsList></div>
        <TabsContent value="targets" className="mt-4"><Targets /></TabsContent>
        <TabsContent value="brands" className="mt-4"><Brands /></TabsContent>
        <TabsContent value="geography" className="mt-4"><Geography /></TabsContent>
        <TabsContent value="dealers" className="mt-4"><Dealers /></TabsContent>
        <TabsContent value="users" className="mt-4"><Users /></TabsContent>
        <TabsContent value="manpower" className="mt-4"><ManpowerAdmin /></TabsContent>
        <TabsContent value="dpc" className="mt-4"><DpcAdmin /></TabsContent>
        <TabsContent value="service" className="mt-4"><ServiceAdmin /></TabsContent>
        <TabsContent value="stock" className="mt-4"><StockAdmin /></TabsContent>
        <TabsContent value="scoring" className="mt-4"><Scoring /></TabsContent>
        <TabsContent value="data" className="mt-4"><DataAdmin /></TabsContent>
        <TabsContent value="usage" className="mt-4"><UsageAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
